import { useQuery } from '@tanstack/react-query';

import { useUniversalNavContext } from './useUniversalNavContext';
import { Props as UniversalLayoutProps } from '../../universal-layout/components/UniversalLayout/UniversalLayout';

import type { UserRole, ZapierSession } from '../../types';

type ZapierSessionRoleRaw = {
  account_id: number;
  role: UserRole;
};

export type ZapierSessionRaw = {
  banner_message: string;
  id: number;
  signup: string;
  current_account_id: number;
  email: string;
  is_logged_in: boolean;
  is_staff?: boolean;
  roles: Array<ZapierSessionRoleRaw>;
  has_seen_notifications: {
    blog_subscription: boolean;
    jobs_cta: boolean;
  };
};

export const prepareSessionData = (json: ZapierSessionRaw): ZapierSession => {
  const currentRole = json.roles?.find((role) => {
    return role.account_id === json.current_account_id;
  })?.role;

  const output = {
    customUserId: json.id,
    bannerMessage: json.banner_message,
    currentAccountId: json.current_account_id,
    isLoggedIn: json.is_logged_in,
    isStaff: json.is_staff,
    email: json.email,
    signupDate: json.signup,
    currentUserRole: currentRole,
    hasSeenNotifications: {
      jobsCta: json.has_seen_notifications?.jobs_cta,
    },
  };

  return output;
};

async function fetchSessionData(apiHostname: string): Promise<ZapierSession> {
  const response = await fetch(`${apiHostname}/api/v4/session`, {
    credentials: process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  const json = await response.json();
  return prepareSessionData(json);
}

export const SESSION_QUERY_KEY = ['session'];

export const useQuerySession = (
  // `sessionData` is passed as an argument because `sessionDataFromProps` will be `undefined` whenever it is called directly from `UniversalNavProvider` or from one of its hooks (assuming a service has passed it as a prop to `UniversalLayout`)
  sessionData?: UniversalLayoutProps['sessionData'],
  apiHostnameFromParams?: string
) => {
  const {
    sessionData: sessionDataFromProps,
    apiHostname: apiHostnameFromContext,
  } = useUniversalNavContext();

  const apiHostname = apiHostnameFromParams || apiHostnameFromContext;

  const result = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => fetchSessionData(apiHostname),
    enabled: !sessionDataFromProps && !sessionData,
    initialData: sessionDataFromProps || sessionData,
    staleTime: 1000 * 10,
  });

  return {
    ...result,
    // https://tanstack.com/query/v4/docs/react/guides/disabling-queries#isinitialloading
    isLoading: result.isInitialLoading,
  };
};
